import { useEffect, useMemo, useRef } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { blocksForDate } from '../../store/selectors'
import { addDaysISO, todayISO } from '../../lib/dates'

/**
 * Two weeks of days, swipeable, with block dots.
 *
 * The calendar is a picker, not a separate view: the parent flicks to
 * yesterday to check something off late, or to tomorrow to see what is
 * coming — without leaving the page they run the day from.
 */
export function DateStrip({ selected, timezone }: { selected: string; timezone?: string }) {
  const today = todayISO(timezone)
  const days = useMemo(() => {
    const out: string[] = []
    for (let i = -7; i <= 7; i++) out.push(addDaysISO(today, i))
    return out
  }, [today])

  // Subscribe once for all dots — a per-day selector would be 15 subscriptions.
  // One quiet dot per day: its fill is the day's state, not a count.
  const stateByDate = useAuroraStore((s: any) => {
    const out: Record<string, 'none' | 'pending' | 'some' | 'all'> = {}
    for (const d of days) {
      const blocks = blocksForDate(s, d)
      if (!blocks.length) { out[d] = 'none'; continue }
      const settled = blocks.filter((b) => b.status && b.status !== 'pending').length
      out[d] = settled === 0 ? 'pending' : settled === blocks.length ? 'all' : 'some'
    }
    return out
  })

  const stripRef = useRef<HTMLDivElement>(null)
  // Keep the selected day in view — on a phone the strip is wider than the
  // screen, and a picker that opens scrolled away from its selection is a maze.
  useEffect(() => {
    const el = stripRef.current?.querySelector('[aria-selected="true"]')
    el?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [selected])

  return (
    <div className="datestrip" role="tablist" aria-label="Pick a day" ref={stripRef}>
      {days.map((date) => {
        const dow = new Date(date + 'T12:00:00Z').toLocaleDateString('en-US', {
          weekday: 'short', timeZone: 'UTC',
        })
        const num = Number(date.slice(8, 10))
        const state = stateByDate[date] ?? 'none'
        return (
          <button
            key={date}
            role="tab"
            aria-selected={date === selected}
            className={`datestrip-day${date === selected ? ' is-selected' : ''}${date === today ? ' is-today' : ''}`}
            onClick={() => (auroraStore.getState() as any).selectDate(date === today ? undefined : date)}
          >
            <span className="datestrip-dow">{dow}</span>
            <span className="datestrip-num">{num}</span>
            <span className="datestrip-dots" aria-hidden="true">
              {state !== 'none' && <span className={`datestrip-state is-${state}`} />}
            </span>
          </button>
        )
      })}
    </div>
  )
}
